/* Kasa · Vídeos decorativos: sin controles, en bucle y solos
 * -----------------------------------------------------------
 * El bloque de vídeo del tema deja el vídeo detrás de un póster con botón de
 * play y con los controles a la vista. Para un vídeo decorativo eso sobra.
 *
 * Esto se hace desde un asset y no desde `blocks/video.liquid` a propósito:
 * la sincronización de Shopify se salta ese archivo (se quedó con la versión
 * de mayo por más que se empujara), mientras que los assets sí suben. Hacerlo
 * en JS es además independiente de la plantilla.
 *
 * No se fuerza la carga de todos al abrir la página, que sería lo caro: se
 * observa cuándo entran en pantalla y solo entonces se cargan y arrancan. Al
 * salir se pausan, así el móvil no descarga ni reproduce lo que nadie mira.
 */
(function () {
  'use strict';

  // Quien pide menos movimiento en su sistema se queda con el póster y su
  // botón de play: sigue pudiendo verlo, pero no le salta solo.
  const quieto = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  /**
   * La galería principal del producto se queda como está.
   *
   * Ahí el póster con su botón de play hace falta: el vídeo tarda en cargar y
   * sin el botón la primera impresión es la de una foto que no hace nada.
   * Además es un vídeo que el cliente decide mirar, no decoración de fondo.
   */
  const FUERA = '.product-media, .product-media-container';

  /** Los vídeos subidos del tema; todos salen con `autoplay` en la etiqueta. */
  function candidatos(raiz) {
    return Array.from(raiz.querySelectorAll?.('deferred-media') || []).filter((el) => {
      if (el.closest(FUERA)) return false;
      const dentro = el.querySelector('template')?.content?.querySelector('video[autoplay]');
      return Boolean(dentro || el.querySelector('video[autoplay]'));
    });
  }

  function prepara(v) {
    if (!v) return;
    v.controls = false;
    v.removeAttribute('controls'); // el atributo viene puesto desde Liquid
    v.playsInline = true;          // sin esto iOS lo abre a pantalla completa
    v.muted = true;                // sin esto ningún navegador deja autoplay
  }

  function reproduce(v) {
    const intento = v?.play();
    if (intento?.catch) intento.catch(() => {}); // si el navegador se niega, ni rastro
  }

  function arranca(el) {
    delete el.dataset.kasaFuera;
    if (typeof el.loadContent === 'function' && !el.getAttribute('data-media-loaded')) {
      el.loadContent(false); // sin robar el foco: el usuario no ha pedido nada
    }
    const v = el.querySelector('video');
    if (!v) return;
    prepara(v);

    // El tema aplica "un vídeo a la vez": cada `deferred-media` escucha un
    // evento global y se pausa cuando otro empieza. Con tres tarjetas una al
    // lado de la otra eso dejaba solo la última en marcha y las demás
    // congeladas en el primer fotograma. Esa política tiene sentido para
    // vídeos con sonido, no para decoración muda en bucle, así que retomamos
    // la reproducción salvo que hayamos sido nosotros quienes la paramos.
    if (!v.dataset.kasaVigilado) {
      v.dataset.kasaVigilado = '1';
      v.addEventListener('pause', () => {
        if (!el.dataset.kasaFuera && el.isConnected) reproduce(v);
      });
    }

    reproduce(v);
  }

  function para(el) {
    el.dataset.kasaFuera = '1'; // esta pausa sí la queremos: está fuera de pantalla
    const v = el.querySelector('video');
    if (v && !v.paused) v.pause();
  }

  function observa(raiz = document) {
    const nodos = candidatos(raiz);
    if (!nodos.length) return;

    // Aunque no se reproduzcan solos, los controles sobran desde ya
    nodos.forEach((el) => {
      el.classList.add('kasa-video-auto');
      prepara(el.querySelector('video'));
    });

    if (quieto || !('IntersectionObserver' in window)) return;

    const obs = new IntersectionObserver(
      (entradas) => entradas.forEach((e) => (e.isIntersecting ? arranca(e.target) : para(e.target))),
      // Un poco antes de que asome, para que no se vea el salto del póster
      { rootMargin: '200px 0px', threshold: 0.01 }
    );
    nodos.forEach((n) => obs.observe(n));
  }

  // El póster es solo un marcador mientras carga: su icono de play sobra.
  const css = document.createElement('style');
  css.textContent =
    '.kasa-video-auto .deferred-media__poster-icon{display:none}' +
    '.kasa-video-auto .deferred-media__poster-button{cursor:default}' +
    '.kasa-video-auto video::-webkit-media-controls{display:none!important}';
  document.head.appendChild(css);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => observa());
  } else {
    observa();
  }

  // Las secciones se repintan al editar el tema y al navegar el carrusel
  document.addEventListener('shopify:section:load', (e) => observa(e.target));
  window.kasaRefreshAutoplayVideos = observa;
})();
